/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/* eslint-disable @atlaskit/design-system/use-primitives-text */
/* eslint-disable @atlaskit/design-system/use-tokens-typography */
/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useEffect, useRef } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { useAnalyticsEvents } from '../../../../common/analytics/generated/use-analytics-events';
import type { AuthState } from '../../../../common/analytics/types';
import { useAtlassianUserContext } from '../../../../hooks/use-user-context';
import { HeroIcons } from '../InteractiveHeroIcons';
import { useMediaQuery } from '../InteractiveHeroIcons/use-media-query';
import whoopsImage from './assets/whoops.png';
import { messages } from './messages';

// `whoops.png` is exported at @2x density (intrinsic 1316x311). The 1x display
// size is therefore 658x155.5, which we use as the upper bound so the image
// never renders larger than its native resolution.
const WHOOPS_INTRINSIC_DISPLAY_WIDTH_PX = 658;

// Mirror the fluid sizing strategy used by the signed-out hero typography in
// `ContentStack.tsx` (`WORDMARK_MAX_WIDTH`/`WORDMARK_WIDE_VIEWPORT_QUERY`):
// gutter the image away from the viewport edges, switching to a wider gutter
// past the same breakpoint, so it scales smoothly with the viewport while
// still being capped at its native 1x size.
const WHOOPS_WIDE_VIEWPORT_QUERY = '(min-width: 769px)';
const WHOOPS_MAX_WIDTH = {
	base: `min(${WHOOPS_INTRINSIC_DISPLAY_WIDTH_PX}px, calc(100vw - 96px))`,
	wide: `min(${WHOOPS_INTRINSIC_DISPLAY_WIDTH_PX}px, calc(100vw - 256px))`,
} as const;

const WHOOPS_DESCRIPTION_MAX_WIDTH = {
	base: 'min(84vw, 390px)',
	wide: 'min(612px, calc(100vw - 256px))',
} as const;

const styles = cssMap({
	placeholder: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
		height: '100%',
	},
	stack: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		width: '100%',
		gap: 'clamp(24px, 4vh, 48px)',
	},
	image: {
		display: 'block',
		width: '100%',
		height: 'auto',
	},
	description: {
		margin: 0,
		textAlign: 'center',
		fontWeight: token('font.weight.regular'),
		letterSpacing: '0.5px',
		color: '#B7B9BE',
		fontFamily: token('font.family.brand.heading'),
		fontSize: 'clamp(20px, 4vw, 24px)',
		lineHeight: 1.5,
		whiteSpace: 'pre-line',
	},
});

type WhoopsPlaceholderProps = {
	testId?: string;
};

/**
 * Centered "Whoops" graphic shown while the error UI is being designed.
 * Renders the @2x PNG asset at a fluid size that mirrors the marketing
 * wordmark in the signed-out hero (`ContentStack`), capped at its 1x size.
 */
function WhoopsPlaceholder({
	testId = 'twg-microsite-error-view',
}: WhoopsPlaceholderProps = {}): React.JSX.Element {
	const { fireEvent } = useAnalyticsEvents();
	const { isAuthed } = useAtlassianUserContext();
	const authState: AuthState = isAuthed ? 'loggedIn' : 'loggedOut';
	const authStateRef = useRef(authState);
	const isWideViewport = useMediaQuery(WHOOPS_WIDE_VIEWPORT_QUERY);
	const imageMaxWidth = isWideViewport ? WHOOPS_MAX_WIDTH.wide : WHOOPS_MAX_WIDTH.base;
	const descriptionMaxWidth = isWideViewport
		? WHOOPS_DESCRIPTION_MAX_WIDTH.wide
		: WHOOPS_DESCRIPTION_MAX_WIDTH.base;

	useEffect(() => {
		authStateRef.current = authState;
	}, [authState]);

	useEffect(() => {
		fireEvent('screen.twgMicrositePageError.viewed', {
			authState: authStateRef.current,
		});
	}, [fireEvent]);

	return (
		<div css={styles.placeholder} data-testid={testId}>
			<div css={styles.stack}>
				<img css={styles.image} src={whoopsImage} alt="" style={{ maxWidth: imageMaxWidth }} />
				<p css={styles.description} style={{ maxWidth: descriptionMaxWidth }}>
					<FormattedMessage {...messages.description} />
				</p>
			</div>
		</div>
	);
}

// oxlint-disable-next-line eslint/no-redeclare
export type PageErrorView = {
	testId?: string;
};

/**
 * Fallback view rendered by the top-level `SentryErrorBoundary` when the
 * Home experience crashes. Reuses the surrounding-icon hero animation with a
 * placeholder "Whoops" centerpiece in place of the marketing wordmark/CTA.
 *
 * Fires `screen.twgMicrositePageError.viewed` once on mount so dashboards
 * can compute a session-level page-crash rate against
 * `screen.twgMicrositeHome.viewed`.
 */
export const PageErrorView = ({ testId = 'page-error-view' }: PageErrorView): React.JSX.Element => {
	return <HeroIcons fitViewport centerContent={<WhoopsPlaceholder testId={testId} />} />;
};
